/**
 * OutletDJ Favourites page renderer. Reads saved product handles from
 * window.OutletDJFavorites (assets/favorites.js, loaded globally in
 * layout/theme.liquid) and fetches each one's live data via Shopify's
 * public /products/{handle}.js endpoint - no backend/app needed.
 *
 * A handle whose fetch 404s (product deleted) is silently dropped from
 * the saved list. A handle that fetches fine but is unavailable
 * (available: false) still renders, marked "No longer available",
 * rather than being hidden - per spec, favorites must never show a
 * broken card, but an out-of-stock item is real information, not a
 * broken one.
 */
(function () {
  'use strict';

  function moneyFromCents(cents) {
    var value = (cents / 100).toFixed(2);
    return '£' + value;
  }

  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } }).then(
      function (res) {
        if (!res.ok) return null;
        return res.json();
      },
      function () {
        return null;
      }
    );
  }

  function buildCard(template, product, handle) {
    var node = template.content.firstElementChild.cloneNode(true);
    var available = !!(product && product.available !== false);
    var missing = !product;

    if (missing) {
      node.setAttribute('data-unavailable', '');
    } else if (!available) {
      node.setAttribute('data-unavailable', '');
    }

    var link = node.querySelector('[data-favorites-card-link]');
    var img = node.querySelector('[data-favorites-card-image]');
    var vendor = node.querySelector('[data-favorites-card-vendor]');
    var title = node.querySelector('[data-favorites-card-title]');
    var price = node.querySelector('[data-favorites-card-price]');
    var rrpRow = node.querySelector('[data-favorites-card-rrp-row]');
    var rrpValue = node.querySelector('[data-favorites-card-rrp]');
    var discount = node.querySelector('[data-favorites-card-discount]');
    var unavailableBadge = node.querySelector('[data-favorites-card-unavailable]');
    var removeBtn = node.querySelector('[data-favorites-card-remove]');

    if (missing) {
      link.setAttribute('href', '#');
      link.setAttribute('aria-disabled', 'true');
      title.textContent = 'This product is no longer available';
      unavailableBadge.hidden = false;
    } else {
      link.setAttribute('href', product.url);
      var featuredImage = product.featured_image || (product.images && product.images[0]);
      if (featuredImage) {
        img.setAttribute('src', featuredImage);
        img.setAttribute('alt', product.title || '');
      }
      vendor.textContent = product.vendor || '';
      title.textContent = product.title || '';

      var variant = (product.variants && product.variants[0]) || {};
      var priceCents = variant.price;
      var compareCents = variant.compare_at_price;

      price.textContent = moneyFromCents(priceCents);

      if (compareCents && compareCents > priceCents) {
        rrpRow.hidden = false;
        rrpValue.textContent = moneyFromCents(compareCents);
        var pct = Math.round(((compareCents - priceCents) / compareCents) * 100);
        if (pct > 0) {
          discount.textContent = ' · ' + pct + '% below RRP';
        }
      }

      if (!available) {
        unavailableBadge.hidden = false;
      }
    }

    removeBtn.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      window.OutletDJFavorites.removeFavorite(handle);
      node.remove();
      renderEmptyStateIfNeeded();
    });

    return node;
  }

  var pageEl = document.querySelector('[data-favorites-page]');

  function renderEmptyStateIfNeeded() {
    if (!pageEl) return;
    var grid = pageEl.querySelector('[data-favorites-grid]');
    var empty = pageEl.querySelector('[data-favorites-empty]');
    var isEmpty = grid.children.length === 0;
    // The visual show/hide is driven by the --visible modifier classes
    // below, not the hidden attribute - Shopify's theme CSS bundler drops
    // "[hidden]" attribute-selector rules from the compiled stylesheet, so
    // a plain hidden-attribute toggle silently fails to hide an element
    // that also has an author "display" rule (flex/grid) on its base
    // class. hidden is still set too, for accessibility semantics.
    empty.hidden = !isEmpty;
    empty.classList.toggle('favorites-page__empty--visible', isEmpty);
    grid.hidden = isEmpty;
    grid.classList.toggle('favorites-page__grid--visible', !isEmpty);
  }

  function init() {
    if (!pageEl || !window.OutletDJFavorites) return;

    var loading = pageEl.querySelector('[data-favorites-loading]');
    var grid = pageEl.querySelector('[data-favorites-grid]');
    var template = document.getElementById('favorites-card-template');
    var handles = window.OutletDJFavorites.readFavorites();

    if (handles.length === 0) {
      renderEmptyStateIfNeeded();
      return;
    }

    loading.hidden = false;

    Promise.all(
      handles.map(function (handle) {
        return fetchProduct(handle).then(function (product) {
          return { handle: handle, product: product };
        });
      })
    ).then(function (results) {
      loading.hidden = true;

      // A deleted/404 product is silently dropped from the saved list -
      // it's not "removed by the user", so no favorites:changed pulse,
      // just a quiet, permanent cleanup of a dead reference.
      var alive = results.filter(function (r) {
        return r.product !== null;
      });
      var deadHandles = results
        .filter(function (r) {
          return r.product === null;
        })
        .map(function (r) {
          return r.handle;
        });

      if (deadHandles.length > 0) {
        var remaining = window.OutletDJFavorites.readFavorites().filter(function (h) {
          return deadHandles.indexOf(h) === -1;
        });
        try {
          window.localStorage.setItem(window.OutletDJFavorites.STORAGE_KEY, JSON.stringify(remaining));
        } catch (e) {
          // ignore
        }
      }

      alive.forEach(function (r) {
        grid.appendChild(buildCard(template, r.product, r.handle));
      });

      renderEmptyStateIfNeeded();
    });
  }

  /**
   * This script tag renders inside <main>, before assets/favorites.js
   * (loaded at the end of layout/theme.liquid) in document order. Module
   * scripts execute in document order once parsing finishes but BEFORE
   * DOMContentLoaded fires - so by the time THIS script runs,
   * document.readyState is already past 'loading' even though
   * favorites.js (and its window.OutletDJFavorites) hasn't executed yet.
   * Waiting for DOMContentLoaded (which fires only after every deferred/
   * module script has run) avoids that race; only skip the wait if the
   * event has demonstrably already fired ('complete').
   */
  if (document.readyState === 'complete') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
